/**
 * Generic "don't run yet" loader for scripts marked type="lazyloadscript" by
 * PHP (see pifagor_lazyload_mark_script_tag() / pifagor_lazyload_mark_inline_script()
 * in inc/enqueue-script-style.php). Browsers never execute a <script> with an
 * unrecognized type, so marking a handle this way makes it fully inert until
 * this file revives it — on the visitor's first interaction, or after a
 * fallback delay, whichever comes first.
 *
 * Which handles are routed through this is decided by
 * pifagor_lazyload_script_handles() in inc/enqueue-script-style.php.
 *
 * No click interception or document.write shim here — unused on this site.
 *
 * What IS kept, and is not optional: the DOMContentLoaded/load event
 * redirect below. Without it, a lazy-loaded script whose own top-level code
 * does `document.addEventListener('DOMContentLoaded', fn)` would simply
 * never run `fn` — the real event already fired long before this code
 * revives the script. Same problem for jQuery(document).ready(fn).
 */
(function () {
	'use strict';

	var SELECTOR = 'script[type="lazyloadscript"]';
	/*
	 * Elements that must be fully interactive the moment they are on screen,
	 * even without any interaction from the visitor. The CTA form is the case
	 * that forced this: its phone mask (intlTelInput) and the custom "Обрати
	 * урок" select are built by lazy handles, so a visitor who reloads while
	 * already scrolled to the form saw the bare native input/select until the
	 * 8-second fallback fired — no interaction happens in that scenario, and
	 * the browser restores the scroll position without emitting a scroll event.
	 */
	var INVIEW_SELECTOR = '.wpcf7-form, [data-lazy-inview]';
	/*
	 * Start loading well before the element is actually on screen: measured on
	 * production the whole chain (revive -> jQuery -> intlTelInput -> widget)
	 * takes ~170ms warm, so ~1000px of lead time means a visitor scrolling to
	 * the form finds the phone mask and the custom select already built,
	 * instead of watching them appear. Readers who never scroll near the form
	 * still never pay for these scripts.
	 */
	var INVIEW_MARGIN = '1000px';
	var triggered = false;
	var fallbackDelay = 8000;
	var events = ['keydown', 'mousedown', 'touchstart', 'scroll', 'wheel'];
	var options = { passive: true };

	function onInteraction() {
		events.forEach(function (e) {
			window.removeEventListener(e, onInteraction, options);
		});
		trigger();
	}

	function trigger() {
		if (triggered) return;
		triggered = true;
		run();
	}

	/**
	 * This script itself prints as a plain blocking <script> (no defer), so
	 * it can execute before the parser has even reached the
	 * type="lazyloadscript" tags further down the document — querying for
	 * SELECTOR at that point always finds nothing and would make this whole
	 * file a no-op, forever, regardless of where in the page it happens to
	 * print relative to any other footer script. Wait for the document to
	 * actually finish parsing before checking, so it doesn't matter what
	 * order scripts print in.
	 */
	function init() {
		if (!document.querySelector(SELECTOR)) {
			return;
		}
		events.forEach(function (e) {
			window.addEventListener(e, onInteraction, options);
		});
		observeInView();
		window.setTimeout(trigger, fallbackDelay);
	}

	/*
	 * Revive as soon as one of the INVIEW_SELECTOR elements comes close to the
	 * viewport. Hidden elements (modals with display:none) never intersect, so
	 * a form inside a closed popup does not pull the bundle in early.
	 */
	function observeInView() {
		if (typeof window.IntersectionObserver !== 'function') return;

		var nodes = document.querySelectorAll(INVIEW_SELECTOR);
		if (!nodes.length) return;

		var observer = new IntersectionObserver(
			function (entries) {
				for (var i = 0; i < entries.length; i++) {
					if (entries[i].isIntersecting) {
						observer.disconnect();
						trigger();
						return;
					}
				}
			},
			{ rootMargin: INVIEW_MARGIN }
		);

		Array.prototype.forEach.call(nodes, function (node) {
			observer.observe(node);
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}

	/**
	 * Redirects addEventListener('DOMContentLoaded'|'load'|'readystatechange'|'pageshow')
	 * on document/window, plus the onreadystatechange/onload/onpageshow
	 * property setters, to a "lazy-*" namespaced equivalent — so a script we
	 * revive later can still register for "the page is ready" and actually
	 * get called, instead of silently listening for an event that already
	 * fired before this script existed.
	 */
	function redirectReadyEvents() {
		[
			[document, 'DOMContentLoaded'],
			[document, 'readystatechange'],
			[window, 'DOMContentLoaded'],
			[window, 'load'],
			[window, 'pageshow'],
		].forEach(function (pair) {
			var target = pair[0];
			var type = pair[1];
			var nativeAdd = target.addEventListener.bind(target);
			var nativeRemove = target.removeEventListener.bind(target);
			target.addEventListener = function (t, listener, opts) {
				nativeAdd(t === type ? 'lazy-' + type : t, listener, opts);
			};
			target.removeEventListener = function (t, listener, opts) {
				nativeRemove(t === type ? 'lazy-' + type : t, listener, opts);
			};
		});

		redirectProperty(document, 'onreadystatechange');
		redirectProperty(window, 'onload');
		redirectProperty(window, 'onpageshow');
	}

	function redirectProperty(target, prop) {
		Object.defineProperty(target, prop, {
			configurable: true,
			get: function () {
				return null;
			},
			set: function (fn) {
				target['lazy' + prop] = fn;
			},
		});
	}

	function dispatchReadyEvents() {
		document.dispatchEvent(new Event('lazy-DOMContentLoaded'));
		document.dispatchEvent(new Event('lazy-readystatechange'));
		if (typeof document.lazyonreadystatechange === 'function') {
			document.lazyonreadystatechange();
		}

		window.dispatchEvent(new Event('lazy-DOMContentLoaded'));
		window.dispatchEvent(new Event('lazy-load'));
		window.dispatchEvent(new Event('lazy-jquery-load'));
		if (typeof window.lazyonload === 'function') {
			window.lazyonload();
		}

		window.dispatchEvent(new Event('lazy-pageshow'));
		if (typeof window.lazyonpageshow === 'function') {
			window.lazyonpageshow();
		}
	}

	/**
	 * Same problem, but for jQuery(document).ready(fn) / $(window).on('load', fn)
	 * in any jQuery plugin script this revives. Only matters if jQuery is
	 * already present — this engine never controls jQuery's own loading,
	 * that stays synchronous and unaffected (see enqueue-script-style.php).
	 */
	function redirectJQueryReady() {
		var jq = window.jQuery;
		if (!jq || !jq.fn || jq._pifagorReadyPatched) return;
		jq._pifagorReadyPatched = true;

		jq.fn.ready = jq.fn.init.prototype.ready = function (fn) {
			document.addEventListener('lazy-DOMContentLoaded', function () {
				fn.call(document, jq);
			});
			return this;
		};

		var nativeOn = jq.fn.on;
		jq.fn.on = jq.fn.init.prototype.on = function (name) {
			if (this[0] === window && typeof name === 'string' && (name === 'load' || name.indexOf('load.') === 0)) {
				arguments[0] = 'lazy-jquery-load';
			}
			return nativeOn.apply(this, arguments);
		};
	}

	function categorize(el, queues) {
		if (!el.hasAttribute('src')) {
			queues.normal.push(el);
			return;
		}
		if (el.hasAttribute('async') && el.async !== false) {
			queues.async.push(el);
		} else if ((el.hasAttribute('defer') && el.defer !== false) || el.getAttribute('data-lazy-type') === 'module') {
			queues.defer.push(el);
		} else {
			queues.normal.push(el);
		}
	}

	function revive(el) {
		return new Promise(function (resolve) {
			var real = document.createElement('script');
			Array.prototype.forEach.call(el.attributes, function (attr) {
				var name = attr.nodeName;
				if (name === 'type') return;
				if (name === 'data-lazy-type') {
					real.setAttribute('type', attr.nodeValue);
					return;
				}
				real.setAttribute(name, attr.nodeValue);
			});

			if (el.hasAttribute('src')) {
				real.addEventListener('load', resolve);
				real.addEventListener('error', resolve);
			} else {
				real.text = el.text;
			}

			el.parentNode.replaceChild(real, el);

			if (!el.hasAttribute('src')) {
				resolve();
			}
		});
	}

	function reviveSequential(list) {
		if (!list.length) return Promise.resolve();
		var el = list.shift();
		return revive(el).then(function () {
			return reviveSequential(list);
		});
	}

	function run() {
		redirectReadyEvents();
		redirectJQueryReady();

		var queues = { normal: [], defer: [], async: [] };
		document.querySelectorAll(SELECTOR).forEach(function (el) {
			categorize(el, queues);
		});

		reviveSequential(queues.normal)
			.then(function () {
				return reviveSequential(queues.defer);
			})
			.then(function () {
				return Promise.all(queues.async.map(revive));
			})
			.then(function () {
				redirectJQueryReady(); // in case a revived script only just loaded jQuery
				dispatchReadyEvents();
				window.dispatchEvent(new Event('lazy-allScriptsLoaded'));
			});
	}
})();
